'use client';

import { useState, useMemo } from 'react';
import Link from 'next/link';
import * as XLSX from 'xlsx';
import { useCommandCenterTool } from '@/lib/command-center/useCommandCenterTool';

const TECH_ROLES = [
  'Software Engineer',
  'Senior Software Engineer',
  'Staff Engineer',
  'Principal Engineer',
  'Engineering Manager',
  'Senior Engineering Manager',
  'Director of Engineering',
  'VP of Engineering',
  'CTO',
  'Frontend Developer',
  'Backend Developer',
  'Full Stack Developer',
  'DevOps Engineer',
  'Site Reliability Engineer (SRE)',
  'Data Engineer',
  'Machine Learning Engineer',
  'Product Manager',
  'Technical Product Manager',
  'QA Engineer',
  'Security Engineer',
];

const EXPERIENCE_LEVELS = [
  { id: 'junior', label: 'Junior (0-2 years)', multiplier: 0.7 },
  { id: 'mid', label: 'Mid-Level (2-5 years)', multiplier: 1.0 },
  { id: 'senior', label: 'Senior (5-8 years)', multiplier: 1.4 },
  { id: 'lead', label: 'Lead (8-12 years)', multiplier: 1.8 },
  { id: 'principal', label: 'Principal (12+ years)', multiplier: 2.2 },
];

const LOCATIONS = [
  // North America - Major Tech Hubs
  { city: 'San Francisco, CA, USA', col: 1.8, baseSalary: 150000, region: 'North America' },
  { city: 'New York, NY, USA', col: 1.7, baseSalary: 140000, region: 'North America' },
  { city: 'Seattle, WA, USA', col: 1.5, baseSalary: 135000, region: 'North America' },
  { city: 'Los Angeles, CA, USA', col: 1.4, baseSalary: 130000, region: 'North America' },
  { city: 'Boston, MA, USA', col: 1.4, baseSalary: 130000, region: 'North America' },
  { city: 'Austin, TX, USA', col: 1.2, baseSalary: 120000, region: 'North America' },
  { city: 'Denver, CO, USA', col: 1.1, baseSalary: 115000, region: 'North America' },
  { city: 'Chicago, IL, USA', col: 1.1, baseSalary: 115000, region: 'North America' },
  { city: 'Atlanta, GA, USA', col: 1.0, baseSalary: 105000, region: 'North America' },
  { city: 'Miami, FL, USA', col: 1.1, baseSalary: 110000, region: 'North America' },
  { city: 'Toronto, Canada', col: 1.2, baseSalary: 100000, region: 'North America' },
  { city: 'Vancouver, Canada', col: 1.3, baseSalary: 95000, region: 'North America' },
  { city: 'Remote (US)', col: 1.0, baseSalary: 110000, region: 'North America' },

  // Western Europe - High Cost
  { city: 'London, UK', col: 1.5, baseSalary: 85000, region: 'Western Europe' },
  { city: 'Zurich, Switzerland', col: 1.9, baseSalary: 120000, region: 'Western Europe' },
  { city: 'Amsterdam, Netherlands', col: 1.3, baseSalary: 75000, region: 'Western Europe' },
  { city: 'Berlin, Germany', col: 1.1, baseSalary: 70000, region: 'Western Europe' },
  { city: 'Munich, Germany', col: 1.3, baseSalary: 75000, region: 'Western Europe' },
  { city: 'Paris, France', col: 1.3, baseSalary: 65000, region: 'Western Europe' },
  { city: 'Dublin, Ireland', col: 1.2, baseSalary: 75000, region: 'Western Europe' },
  { city: 'Stockholm, Sweden', col: 1.2, baseSalary: 70000, region: 'Western Europe' },
  { city: 'Copenhagen, Denmark', col: 1.3, baseSalary: 75000, region: 'Western Europe' },
  { city: 'Oslo, Norway', col: 1.4, baseSalary: 80000, region: 'Western Europe' },
  { city: 'Helsinki, Finland', col: 1.1, baseSalary: 65000, region: 'Western Europe' },
  { city: 'Brussels, Belgium', col: 1.1, baseSalary: 60000, region: 'Western Europe' },
  { city: 'Vienna, Austria', col: 1.1, baseSalary: 60000, region: 'Western Europe' },

  // Southern Europe
  { city: 'Lisbon, Portugal', col: 0.7, baseSalary: 45000, region: 'Southern Europe' },
  { city: 'Porto, Portugal', col: 0.65, baseSalary: 42000, region: 'Southern Europe' },
  { city: 'Barcelona, Spain', col: 0.8, baseSalary: 50000, region: 'Southern Europe' },
  { city: 'Madrid, Spain', col: 0.85, baseSalary: 52000, region: 'Southern Europe' },
  { city: 'Milan, Italy', col: 0.9, baseSalary: 50000, region: 'Southern Europe' },
  { city: 'Rome, Italy', col: 0.8, baseSalary: 45000, region: 'Southern Europe' },
  { city: 'Athens, Greece', col: 0.6, baseSalary: 35000, region: 'Southern Europe' },

  // Eastern Europe - Outsourcing Hubs
  { city: 'Warsaw, Poland', col: 0.55, baseSalary: 40000, region: 'Eastern Europe' },
  { city: 'Krakow, Poland', col: 0.5, baseSalary: 38000, region: 'Eastern Europe' },
  { city: 'Kyiv, Ukraine', col: 0.35, baseSalary: 30000, region: 'Eastern Europe' },
  { city: 'Lviv, Ukraine', col: 0.3, baseSalary: 28000, region: 'Eastern Europe' },
  { city: 'Bucharest, Romania', col: 0.45, baseSalary: 35000, region: 'Eastern Europe' },
  { city: 'Cluj-Napoca, Romania', col: 0.4, baseSalary: 33000, region: 'Eastern Europe' },
  { city: 'Sofia, Bulgaria', col: 0.35, baseSalary: 28000, region: 'Eastern Europe' },
  { city: 'Prague, Czech Republic', col: 0.6, baseSalary: 45000, region: 'Eastern Europe' },
  { city: 'Budapest, Hungary', col: 0.5, baseSalary: 38000, region: 'Eastern Europe' },
  { city: 'Belgrade, Serbia', col: 0.35, baseSalary: 28000, region: 'Eastern Europe' },
  { city: 'Tallinn, Estonia', col: 0.55, baseSalary: 40000, region: 'Eastern Europe' },
  { city: 'Riga, Latvia', col: 0.45, baseSalary: 32000, region: 'Eastern Europe' },
  { city: 'Vilnius, Lithuania', col: 0.45, baseSalary: 35000, region: 'Eastern Europe' },

  // Asia Pacific - Major Hubs
  { city: 'Singapore', col: 1.3, baseSalary: 80000, region: 'Asia Pacific' },
  { city: 'Tokyo, Japan', col: 1.2, baseSalary: 70000, region: 'Asia Pacific' },
  { city: 'Sydney, Australia', col: 1.3, baseSalary: 90000, region: 'Asia Pacific' },
  { city: 'Melbourne, Australia', col: 1.2, baseSalary: 85000, region: 'Asia Pacific' },
  { city: 'Hong Kong', col: 1.4, baseSalary: 75000, region: 'Asia Pacific' },
  { city: 'Seoul, South Korea', col: 0.9, baseSalary: 55000, region: 'Asia Pacific' },
  { city: 'Taipei, Taiwan', col: 0.7, baseSalary: 45000, region: 'Asia Pacific' },
  { city: 'Auckland, New Zealand', col: 1.0, baseSalary: 70000, region: 'Asia Pacific' },

  // South & Southeast Asia - Outsourcing Hubs
  { city: 'Bangalore, India', col: 0.25, baseSalary: 22000, region: 'South Asia' },
  { city: 'Hyderabad, India', col: 0.22, baseSalary: 20000, region: 'South Asia' },
  { city: 'Pune, India', col: 0.22, baseSalary: 20000, region: 'South Asia' },
  { city: 'Chennai, India', col: 0.2, baseSalary: 18000, region: 'South Asia' },
  { city: 'Delhi NCR, India', col: 0.25, baseSalary: 22000, region: 'South Asia' },
  { city: 'Mumbai, India', col: 0.28, baseSalary: 24000, region: 'South Asia' },
  { city: 'Manila, Philippines', col: 0.25, baseSalary: 18000, region: 'Southeast Asia' },
  { city: 'Cebu, Philippines', col: 0.2, baseSalary: 15000, region: 'Southeast Asia' },
  { city: 'Ho Chi Minh City, Vietnam', col: 0.22, baseSalary: 18000, region: 'Southeast Asia' },
  { city: 'Hanoi, Vietnam', col: 0.2, baseSalary: 16000, region: 'Southeast Asia' },
  { city: 'Jakarta, Indonesia', col: 0.25, baseSalary: 18000, region: 'Southeast Asia' },
  { city: 'Kuala Lumpur, Malaysia', col: 0.35, baseSalary: 28000, region: 'Southeast Asia' },
  { city: 'Bangkok, Thailand', col: 0.3, baseSalary: 22000, region: 'Southeast Asia' },

  // Latin America - Nearshore Hubs
  { city: 'Mexico City, Mexico', col: 0.4, baseSalary: 35000, region: 'Latin America' },
  { city: 'Guadalajara, Mexico', col: 0.35, baseSalary: 32000, region: 'Latin America' },
  { city: 'Monterrey, Mexico', col: 0.38, baseSalary: 34000, region: 'Latin America' },
  { city: 'São Paulo, Brazil', col: 0.45, baseSalary: 35000, region: 'Latin America' },
  { city: 'Rio de Janeiro, Brazil', col: 0.4, baseSalary: 32000, region: 'Latin America' },
  { city: 'Buenos Aires, Argentina', col: 0.35, baseSalary: 28000, region: 'Latin America' },
  { city: 'Bogotá, Colombia', col: 0.3, baseSalary: 25000, region: 'Latin America' },
  { city: 'Medellín, Colombia', col: 0.28, baseSalary: 24000, region: 'Latin America' },
  { city: 'Lima, Peru', col: 0.3, baseSalary: 22000, region: 'Latin America' },
  { city: 'Santiago, Chile', col: 0.4, baseSalary: 32000, region: 'Latin America' },
  { city: 'San José, Costa Rica', col: 0.35, baseSalary: 30000, region: 'Latin America' },
  { city: 'Montevideo, Uruguay', col: 0.35, baseSalary: 30000, region: 'Latin America' },

  // Middle East & Africa
  { city: 'Tel Aviv, Israel', col: 1.2, baseSalary: 80000, region: 'Middle East' },
  { city: 'Dubai, UAE', col: 1.0, baseSalary: 70000, region: 'Middle East' },
  { city: 'Cairo, Egypt', col: 0.2, baseSalary: 15000, region: 'Middle East' },
  { city: 'Cape Town, South Africa', col: 0.35, baseSalary: 30000, region: 'Africa' },
  { city: 'Johannesburg, South Africa', col: 0.35, baseSalary: 32000, region: 'Africa' },
  { city: 'Lagos, Nigeria', col: 0.25, baseSalary: 20000, region: 'Africa' },
  { city: 'Nairobi, Kenya', col: 0.25, baseSalary: 18000, region: 'Africa' },

  // Remote Options
  { city: 'Remote (Global Average)', col: 0.7, baseSalary: 60000, region: 'Remote' },
  { city: 'Remote (Western Europe)', col: 1.1, baseSalary: 70000, region: 'Remote' },
  { city: 'Remote (Eastern Europe)', col: 0.45, baseSalary: 35000, region: 'Remote' },
  { city: 'Remote (Asia)', col: 0.3, baseSalary: 25000, region: 'Remote' },
  { city: 'Remote (LATAM)', col: 0.35, baseSalary: 30000, region: 'Remote' },
];

const EQUITY_TYPES = [
  { id: 'none', label: 'No Equity', value: 0 },
  { id: 'iso', label: 'ISO (Incentive Stock Options)', value: 0.1 },
  { id: 'nso', label: 'NSO (Non-Qualified Stock Options)', value: 0.08 },
  { id: 'rsu', label: 'RSU (Restricted Stock Units)', value: 0.12 },
  { id: 'profit_sharing', label: 'Profit Sharing', value: 0.05 },
];

const CURRENCIES = [
  { code: 'USD', symbol: '$', name: 'US Dollar', rate: 1 },
  { code: 'EUR', symbol: '€', name: 'Euro', rate: 0.92 },
  { code: 'GBP', symbol: '£', name: 'British Pound', rate: 0.79 },
  { code: 'CHF', symbol: 'CHF', name: 'Swiss Franc', rate: 0.88 },
  { code: 'CAD', symbol: 'C$', name: 'Canadian Dollar', rate: 1.36 },
  { code: 'AUD', symbol: 'A$', name: 'Australian Dollar', rate: 1.53 },
  { code: 'JPY', symbol: '¥', name: 'Japanese Yen', rate: 149 },
  { code: 'INR', symbol: '₹', name: 'Indian Rupee', rate: 83 },
  { code: 'BRL', symbol: 'R$', name: 'Brazilian Real', rate: 4.97 },
  { code: 'MXN', symbol: 'MX$', name: 'Mexican Peso', rate: 17.2 },
  { code: 'PLN', symbol: 'zł', name: 'Polish Zloty', rate: 4.0 },
  { code: 'SGD', symbol: 'S$', name: 'Singapore Dollar', rate: 1.34 },
  { code: 'HKD', symbol: 'HK$', name: 'Hong Kong Dollar', rate: 7.82 },
  { code: 'SEK', symbol: 'kr', name: 'Swedish Krona', rate: 10.5 },
  { code: 'NOK', symbol: 'kr', name: 'Norwegian Krone', rate: 10.7 },
  { code: 'DKK', symbol: 'kr', name: 'Danish Krone', rate: 6.9 },
  { code: 'ZAR', symbol: 'R', name: 'South African Rand', rate: 18.5 },
  { code: 'PHP', symbol: '₱', name: 'Philippine Peso', rate: 56 },
  { code: 'VND', symbol: '₫', name: 'Vietnamese Dong', rate: 24500 },
  { code: 'THB', symbol: '฿', name: 'Thai Baht', rate: 35 },
  { code: 'ILS', symbol: '₪', name: 'Israeli Shekel', rate: 3.7 },
  { code: 'AED', symbol: 'د.إ', name: 'UAE Dirham', rate: 3.67 },
];

// Group locations by region for better UX
const LOCATION_REGIONS = [...new Set(LOCATIONS.map(l => l.region))];

export default function SalaryBandCalculator() {
  const [role, setRole] = useState('Software Engineer');
  const [experience, setExperience] = useState('mid');
  const [location, setLocation] = useState(LOCATIONS.find(l => l.city === 'Remote (US)') || LOCATIONS[0]);
  const [equityType, setEquityType] = useState('none');
  const [currency, setCurrency] = useState(CURRENCIES[0]); // USD default
  const [customBaseSalary, setCustomBaseSalary] = useState('');
  const [saved, setSaved] = useState(false);

  // Command Center integration
  const {
    enabled: commandCenterEnabled,
    saving,
    error: saveError,
    saveResult,
  } = useCommandCenterTool({
    toolId: 'salary-band-calculator',
    toolCategory: 'calculator',
  });

  const calculations = useMemo(() => {
    const expLevel = EXPERIENCE_LEVELS.find((e) => e.id === experience);
    const equity = EQUITY_TYPES.find((e) => e.id === equityType);

    // Use custom base salary if provided, otherwise use location default
    const baseSalaryUSD = customBaseSalary ? parseFloat(customBaseSalary) : location.baseSalary;

    // Calculate salary range in USD first
    const adjustedSalaryUSD = baseSalaryUSD * (expLevel?.multiplier || 1.0) * location.col;

    // Convert to selected currency
    const convertToCurrency = (usdAmount: number) => Math.round(usdAmount * currency.rate);

    const minSalary = convertToCurrency(adjustedSalaryUSD * 0.85);
    const midSalary = convertToCurrency(adjustedSalaryUSD);
    const maxSalary = convertToCurrency(adjustedSalaryUSD * 1.15);

    // Calculate equity value (percentage of base salary)
    const equityValue = convertToCurrency(adjustedSalaryUSD * (equity?.value || 0));

    // Total compensation
    const totalCompMin = minSalary + equityValue;
    const totalCompMid = midSalary + equityValue;
    const totalCompMax = maxSalary + equityValue;

    // Annual costs to employer (including benefits ~30%)
    const employerCostMin = Math.round(totalCompMin * 1.3);
    const employerCostMid = Math.round(totalCompMid * 1.3);
    const employerCostMax = Math.round(totalCompMax * 1.3);

    return {
      minSalary,
      midSalary,
      maxSalary,
      equityValue,
      totalCompMin,
      totalCompMid,
      totalCompMax,
      employerCostMin,
      employerCostMid,
      employerCostMax,
      baseSalaryUSD,
    };
  }, [experience, location, equityType, customBaseSalary, currency]);

  // Format currency with proper symbol
  const formatCurrency = (amount: number) => {
    // For currencies with large values (like JPY, VND), don't show decimals
    if (currency.rate > 10) {
      return `${currency.symbol}${amount.toLocaleString()}`;
    }
    return `${currency.symbol}${amount.toLocaleString()}`;
  };

  // Save to Command Center
  const handleSaveToCommandCenter = async () => {
    if (!commandCenterEnabled) return;

    const expLevel = EXPERIENCE_LEVELS.find((e) => e.id === experience);
    const equity = EQUITY_TYPES.find((e) => e.id === equityType);

    const assessment = await saveResult({
      name: `Salary Band - ${role} (${expLevel?.label || experience}) - ${new Date().toLocaleDateString()}`,
      score_overall: calculations.midSalary,
      scores: {
        'Minimum Salary': calculations.minSalary,
        'Midpoint Salary': calculations.midSalary,
        'Maximum Salary': calculations.maxSalary,
        'Equity Value': calculations.equityValue,
        'Total Comp (Min)': calculations.totalCompMin,
        'Total Comp (Mid)': calculations.totalCompMid,
        'Total Comp (Max)': calculations.totalCompMax,
        'Employer Cost (Min)': calculations.employerCostMin,
        'Employer Cost (Mid)': calculations.employerCostMid,
        'Employer Cost (Max)': calculations.employerCostMax,
      },
      answers: {
        role,
        experienceLevel: experience,
        experienceLevelLabel: expLevel?.label,
        location: location.city,
        costOfLivingMultiplier: location.col,
        equityType,
        equityTypeLabel: equity?.label,
        customBaseSalary: customBaseSalary || null,
      },
      metadata: {
        baseSalary: customBaseSalary ? parseFloat(customBaseSalary) : location.baseSalary,
        experienceMultiplier: expLevel?.multiplier,
        equityPercentage: equity?.value,
        benefitsOverhead: 0.3,
        salaryBand: {
          min: calculations.minSalary,
          mid: calculations.midSalary,
          max: calculations.maxSalary,
        },
        totalCompensation: {
          min: calculations.totalCompMin,
          mid: calculations.totalCompMid,
          max: calculations.totalCompMax,
        },
        employerCosts: {
          min: calculations.employerCostMin,
          mid: calculations.employerCostMid,
          max: calculations.employerCostMax,
        },
      },
    });

    if (assessment) {
      setSaved(true);
    }
  };

  const exportToExcel = () => {
    const wb = XLSX.utils.book_new();
    if (!wb.Workbook) wb.Workbook = {};
    (wb.Workbook as any).CalcPr = { fullCalcOnLoad: true };

    // Summary sheet
    const summaryData = [
      ['Salary Band Calculator Report'],
      ['Generated on:', new Date().toLocaleDateString()],
      [],
      ['Position Details'],
      ['Role:', role],
      ['Experience Level:', EXPERIENCE_LEVELS.find((e) => e.id === experience)?.label],
      ['Location:', location.city],
      ['Region:', location.region],
      ['Cost of Living Multiplier:', location.col],
      ['Equity Type:', EQUITY_TYPES.find((e) => e.id === equityType)?.label],
      ['Currency:', `${currency.code} (${currency.name})`],
      [],
      [`Salary Band (${currency.code})`],
      ['Minimum:', formatCurrency(calculations.minSalary)],
      ['Midpoint:', formatCurrency(calculations.midSalary)],
      ['Maximum:', formatCurrency(calculations.maxSalary)],
      [],
      [`Equity & Total Compensation (${currency.code})`],
      ['Annual Equity Value:', formatCurrency(calculations.equityValue)],
      ['Total Comp (Min):', formatCurrency(calculations.totalCompMin)],
      ['Total Comp (Mid):', formatCurrency(calculations.totalCompMid)],
      ['Total Comp (Max):', formatCurrency(calculations.totalCompMax)],
      [],
      [`Employer Costs (${currency.code}) - with 30% benefits`],
      ['Cost (Min):', formatCurrency(calculations.employerCostMin)],
      ['Cost (Mid):', formatCurrency(calculations.employerCostMid)],
      ['Cost (Max):', formatCurrency(calculations.employerCostMax)],
      [],
      ['Note:', 'Base salaries are calculated in USD and converted to selected currency at approximate rates.'],
    ];

    const summarySheet = XLSX.utils.aoa_to_sheet(summaryData);
    XLSX.utils.book_append_sheet(wb, summarySheet, 'Summary');

    // Download
    XLSX.writeFile(wb, `salary-band-${role.replace(/\s+/g, '-')}-${location.city.replace(/[,\s]+/g, '-')}-${Date.now()}.xlsx`);
  };

  return (
    <div className="container mx-auto px-4 py-12 max-w-6xl">
      <div className="mb-8">
        <Link href="/tools" className="text-accent hover:text-accent transition-colors">
          ← Back to Tools
        </Link>
      </div>

      <div className="mb-12">
        <h1 className="text-4xl font-bold text-light-text dark:text-foreground mb-4">
          Salary Band Calculator
        </h1>
        <p className="text-xl text-light-text-secondary dark:text-foreground-secondary">
          Generate competitive salary bands by role, level, and location with equity compensation modeling
        </p>
      </div>

      <div className="grid lg:grid-cols-2 gap-8">
        {/* Input Form */}
        <div className="space-y-6">
          <div className="bg-white dark:bg-surface p-6 rounded-lg border border-gray-200 dark:border-border">
            <h2 className="text-2xl font-bold text-light-text dark:text-foreground mb-6">Position Details</h2>

            <div className="space-y-4">
              {/* Role Selection */}
              <div>
                <label className="block text-sm font-semibold text-light-text dark:text-foreground mb-2">
                  Role
                </label>
                <select
                  value={role}
                  onChange={(e) => setRole(e.target.value)}
                  className="w-full px-4 py-2 bg-white border border-gray-300 dark:border-border rounded text-gray-900"
                >
                  {TECH_ROLES.map((r) => (
                    <option key={r} value={r} className="text-gray-900 bg-white">
                      {r}
                    </option>
                  ))}
                </select>
              </div>

              {/* Experience Level */}
              <div>
                <label className="block text-sm font-semibold text-light-text dark:text-foreground mb-2">
                  Experience Level
                </label>
                <select
                  value={experience}
                  onChange={(e) => setExperience(e.target.value)}
                  className="w-full px-4 py-2 bg-white border border-gray-300 dark:border-border rounded text-gray-900"
                >
                  {EXPERIENCE_LEVELS.map((level) => (
                    <option key={level.id} value={level.id} className="text-gray-900 bg-white">
                      {level.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Location */}
              <div>
                <label className="block text-sm font-semibold text-light-text dark:text-foreground mb-2">
                  Location
                </label>
                <select
                  value={location.city}
                  onChange={(e) => {
                    const loc = LOCATIONS.find((l) => l.city === e.target.value);
                    if (loc) setLocation(loc);
                  }}
                  className="w-full px-4 py-2 bg-white border border-gray-300 dark:border-border rounded text-gray-900"
                >
                  {LOCATION_REGIONS.map((region) => (
                    <optgroup key={region} label={region} className="text-gray-900 bg-white font-semibold">
                      {LOCATIONS.filter((l) => l.region === region).map((loc) => (
                        <option key={loc.city} value={loc.city} className="text-gray-900 bg-white">
                          {loc.city} (COL: {loc.col}x)
                        </option>
                      ))}
                    </optgroup>
                  ))}
                </select>
                <p className="text-xs text-light-text-secondary dark:text-foreground-tertiary mt-1">
                  COL = Cost of Living multiplier (relative to US average)
                </p>
              </div>

              {/* Currency */}
              <div>
                <label className="block text-sm font-semibold text-light-text dark:text-foreground mb-2">
                  Display Currency
                </label>
                <select
                  value={currency.code}
                  onChange={(e) => {
                    const cur = CURRENCIES.find((c) => c.code === e.target.value);
                    if (cur) setCurrency(cur);
                  }}
                  className="w-full px-4 py-2 bg-white border border-gray-300 dark:border-border rounded text-gray-900"
                >
                  {CURRENCIES.map((cur) => (
                    <option key={cur.code} value={cur.code} className="text-gray-900 bg-white">
                      {cur.symbol} {cur.code} - {cur.name}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-light-text-secondary dark:text-foreground-tertiary mt-1">
                  Base salaries are in USD, converted at approximate rates
                </p>
              </div>

              {/* Custom Base Salary (Optional) */}
              <div>
                <label className="block text-sm font-semibold text-light-text dark:text-foreground mb-2">
                  Custom Base Salary in USD (Optional)
                </label>
                <input
                  type="number" inputMode="numeric"
                  value={customBaseSalary}
                  onChange={(e) => setCustomBaseSalary(e.target.value)}
                  placeholder={`Default: $${location.baseSalary.toLocaleString()} USD`}
                  className="w-full px-4 py-2 bg-white border border-gray-300 dark:border-border rounded text-gray-900 placeholder-gray-500"
                />
              </div>

              {/* Equity Type */}
              <div>
                <label className="block text-sm font-semibold text-light-text dark:text-foreground mb-2">
                  Equity Compensation
                </label>
                <select
                  value={equityType}
                  onChange={(e) => setEquityType(e.target.value)}
                  className="w-full px-4 py-2 bg-white border border-gray-300 dark:border-border rounded text-gray-900"
                >
                  {EQUITY_TYPES.map((eq) => (
                    <option key={eq.id} value={eq.id} className="text-gray-900 bg-white">
                      {eq.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Premium Features Promo */}
          <div className="bg-accent-muted border border-border rounded-lg p-6">
            <h3 className="text-lg font-bold text-accent mb-3">🔒 Pro Features</h3>
            <ul className="space-y-2 text-sm text-light-text-secondary dark:text-foreground-secondary">
              <li>• 50+ tech roles and levels</li>
              <li>• Multi-country salary bands</li>
              <li>• Real-time market data integration</li>
              <li>• Promotion impact modeling</li>
              <li>• Excel export with formulas</li>
            </ul>
            <Link
              href="/pricing"
              className="mt-4 block text-center bg-accent text-accent-foreground px-4 py-2 rounded font-semibold hover:bg-accent-hover transition-colors"
            >
              Upgrade to Pro
            </Link>
          </div>
        </div>

        {/* Results */}
        <div className="space-y-6">
          {/* Salary Band */}
          <div className="bg-white dark:bg-surface p-6 rounded-lg border border-gray-200 dark:border-border">
            <h2 className="text-2xl font-bold text-light-text dark:text-foreground mb-4">
              Salary Band
              <span className="text-sm font-normal text-light-text-secondary dark:text-foreground-tertiary ml-2">({currency.code})</span>
            </h2>
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-light-text-secondary dark:text-foreground-secondary">Minimum:</span>
                <span className="text-2xl font-bold text-light-text dark:text-foreground">
                  {formatCurrency(calculations.minSalary)}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-light-text-secondary dark:text-foreground-secondary">Midpoint:</span>
                <span className="text-2xl font-bold text-accent">
                  {formatCurrency(calculations.midSalary)}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-light-text-secondary dark:text-foreground-secondary">Maximum:</span>
                <span className="text-2xl font-bold text-light-text dark:text-foreground">
                  {formatCurrency(calculations.maxSalary)}
                </span>
              </div>
            </div>
          </div>

          {/* Total Compensation */}
          <div className="bg-white dark:bg-surface p-6 rounded-lg border border-gray-200 dark:border-border">
            <h2 className="text-2xl font-bold text-light-text dark:text-foreground mb-4">
              Total Compensation
              <span className="text-sm font-normal text-light-text-secondary dark:text-foreground-tertiary ml-2">({currency.code})</span>
            </h2>
            <div className="space-y-3">
              {calculations.equityValue > 0 && (
                <div className="pb-3 border-b border-gray-200 dark:border-border">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-light-text-secondary dark:text-foreground-secondary">
                      Annual Equity Value:
                    </span>
                    <span className="text-lg font-semibold text-accent">
                      {formatCurrency(calculations.equityValue)}
                    </span>
                  </div>
                </div>
              )}
              <div className="flex justify-between items-center">
                <span className="text-light-text-secondary dark:text-foreground-secondary">Min Total:</span>
                <span className="text-xl font-bold text-light-text dark:text-foreground">
                  {formatCurrency(calculations.totalCompMin)}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-light-text-secondary dark:text-foreground-secondary">Mid Total:</span>
                <span className="text-xl font-bold text-accent">
                  {formatCurrency(calculations.totalCompMid)}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-light-text-secondary dark:text-foreground-secondary">Max Total:</span>
                <span className="text-xl font-bold text-light-text dark:text-foreground">
                  {formatCurrency(calculations.totalCompMax)}
                </span>
              </div>
            </div>
          </div>

          {/* Employer Costs */}
          <div className="bg-white dark:bg-surface p-6 rounded-lg border border-gray-200 dark:border-border">
            <h2 className="text-2xl font-bold text-light-text dark:text-foreground mb-2">
              Employer Costs
              <span className="text-sm font-normal text-light-text-secondary dark:text-foreground-tertiary ml-2">({currency.code})</span>
            </h2>
            <p className="text-sm text-light-text-secondary dark:text-foreground-tertiary mb-4">
              Includes ~30% for benefits, taxes, and overhead
            </p>
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-light-text-secondary dark:text-foreground-secondary">Min Cost:</span>
                <span className="text-xl font-bold text-light-text dark:text-foreground">
                  {formatCurrency(calculations.employerCostMin)}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-light-text-secondary dark:text-foreground-secondary">Mid Cost:</span>
                <span className="text-xl font-bold text-accent">
                  {formatCurrency(calculations.employerCostMid)}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-light-text-secondary dark:text-foreground-secondary">Max Cost:</span>
                <span className="text-xl font-bold text-light-text dark:text-foreground">
                  {formatCurrency(calculations.employerCostMax)}
                </span>
              </div>
            </div>
          </div>

          {/* Export Button */}
          <button
            onClick={exportToExcel}
            className="w-full bg-accent text-accent-foreground px-6 py-3 rounded-lg font-semibold hover:bg-accent-hover transition-colors"
          >
            Export to Excel
          </button>

          {/* Command Center Save Button */}
          <div className="flex flex-wrap gap-4">
            {commandCenterEnabled && !saved && (
              <button
                onClick={handleSaveToCommandCenter}
                disabled={saving}
                className="w-full px-6 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {saving ? 'Saving...' : 'Save to Command Center'}
              </button>
            )}
            {saved && (
              <div className="w-full px-6 py-3 bg-green-600/20 text-green-400 rounded-lg font-medium flex items-center justify-center gap-2">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                Saved to Command Center
              </div>
            )}
            {saveError && (
              <div className="w-full px-6 py-3 bg-red-600/20 text-red-400 rounded-lg text-sm text-center">
                {saveError}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* How It Works */}
      <div className="mt-12 bg-white dark:bg-surface p-8 rounded-lg border border-gray-200 dark:border-border">
        <h2 className="text-2xl font-bold text-light-text dark:text-foreground mb-4">How It Works</h2>
        <div className="grid md:grid-cols-2 gap-6 text-light-text-secondary dark:text-foreground-secondary">
          <div>
            <h3 className="font-semibold text-light-text dark:text-foreground mb-2">Salary Band Calculation</h3>
            <p className="text-sm">
              Base salary is adjusted by experience level multiplier and location cost of living.
              The band spans ±15% around the midpoint to allow for performance differentiation.
            </p>
          </div>
          <div>
            <h3 className="font-semibold text-light-text dark:text-foreground mb-2">Equity Compensation</h3>
            <p className="text-sm">
              Equity value is calculated as a percentage of base salary. RSUs typically offer ~12%,
              ISOs ~10%, NSOs ~8%, and profit sharing ~5% of annual base salary.
            </p>
          </div>
          <div>
            <h3 className="font-semibold text-light-text dark:text-foreground mb-2">Cost of Living</h3>
            <p className="text-sm">
              Location multipliers adjust for regional cost of living differences. San Francisco
              (1.8x) and New York (1.7x) command premiums, while other markets scale proportionally.
            </p>
          </div>
          <div>
            <h3 className="font-semibold text-light-text dark:text-foreground mb-2">Employer Costs</h3>
            <p className="text-sm">
              True employer cost includes ~30% overhead for benefits (health, dental, 401k),
              payroll taxes, equipment, office space, and administrative expenses.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
